import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'recompose';
import moment from 'moment';
import { map, invert, find, isEmpty } from 'lodash';
import { withStyles } from '@material-ui/core';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';

import { Helmet } from 'react-helmet';
import { showDrawer, hideDrawer } from 'containers/DrawerManager/actions';
import { selectIsLoading, selectSiteName } from 'containers/Landing/selectors';
import { AUTOPAY_SIGN_UP } from 'containers/PrimoAccount/NextDelivery/MakePayment/constants';
import { Tabs } from 'components/Ui';
import Loader from 'components/Loader';
import MarketingPromotion from 'components/ContentfulFields/MarketingPromotion';
import AutopaySignUpDrawer from 'containers/PrimoAccount/NextDelivery/MakePayment/AutopaySignUp';
import PayBillDrawer from './NextDelivery/MakePayment/PayBillDrawer';
import AccountHeader from './PrimoCommon/AccountHeader';
import PastOrders from './PastOrders';
import NextDelivery from './NextDelivery/Loadable';
import MyPromotions from './MyPromotions/Loadable';
import FutureOrders from './FutureOrders';
import { selectIsSubscriber, selectSelectedServiceLocation } from './selectors';

const ACCOUNT_ROUTES = (isMobile, isSubscriber) => {
  const routes = {
    next_delivery: {
      path: 'next-delivery',
      name: 'Next Delivery',
      component: <NextDelivery />,
    },
    past_orders: {
      path: 'past-orders',
      name: isMobile ? 'Billing & History' : 'Billing & Delivery History',
      component: <PastOrders />,
    },
    future_orders: {
      path: 'future-orders',
      name: 'Future Deliveries',
      component: <FutureOrders />,
    },
    my_promotions: {
      path: 'my-promotions',
      name: isMobile ? 'My Promos' : 'My Promotions',
      component: <MyPromotions />,
    },
  };

  // Filter routes based on isSubscriber
  return isSubscriber ? routes : { past_orders: routes.past_orders };
};

const ACCOUNT_TAB_MAPPING = (isMobile, isSubscriber) => {
  const routes = ACCOUNT_ROUTES(isMobile, isSubscriber);
  return Object.keys(routes).reduce((mapping, key, index) => {
    mapping[routes[key].path] = index;
    return mapping;
  }, {});
};

const formatDate = (date) => moment(date).format('MMM D, YYYY');

const INVERTED_ACCOUNT_TABS = (isMobile, isSubscriber) => {
  const tabMapping = ACCOUNT_TAB_MAPPING(isMobile, isSubscriber);
  return invert(tabMapping);
};

const jsStyles = (theme) => ({
  tabsRoot: {
    minHeight: '4.6875rem',
    [theme.breakpoints.down('md')]: {
      minHeight: '3.75rem',
    },
    [theme.breakpoints.up('lg')]: {
      padding: '0 3.125rem 0 3.125rem',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '0 6.25rem 0 6.25rem',
    },
  },
  tabRoot: {
    backgroundColor: theme.colors.primaryIce,
    maxWidth: 'none',
    textTransform: 'none',
    color: theme.colors.middleBlue,
    borderRadius: '0px',
    borderColor: theme.colors.primaryBlue,
    borderWidth: '0px 2px 0px 2px',
    transition: theme.transition,
    textAlign: 'left',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    fontSize: '1.25rem',
    lineHeight: '1.25rem',
    minHeight: 76,
    minWidth: '0px',
    paddingBottom: '0.5rem',
    '&:focus, &:hover': {
      borderWidth: '0px 2px 0px 2px',
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 60,
      paddingLeft: '0.625rem',
      paddingRight: '0.625rem',
      fontSize: '0.625rem',
      lineHeight: '0.625rem',
      '& .MuiTab-wrapper': {
        flexDirection: 'column',
      },
    },
    '&:first-child': {
      borderWidth: '0px 2px 0px 0px',
    },
    '&:last-child': {
      borderWidth: '0px 0px 0px 2px',
    },
  },
  tabSelected: {
    background: theme.colors.whiteGrey,
    color: `${theme.colors.black} !important`,
    transition: theme.transition,
    '&:focus, &:hover, &:active': {
      color: theme.colors.black,
      backgroundColor: theme.colors.whiteGrey,
    },
    boxShadow: 'none',
  },
  root: {
    background: theme.colors.primaryBlue,
  },
  tabsIndicator: {
    display: 'none',
  },
  labelContainer: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  flexContainer: {
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    },
  },
});

class PrimoAccount extends Component {
  accountTabMapping = ACCOUNT_TAB_MAPPING(
    this.props.isMobile,
    this.props.isSubscriber,
  );

  state = {
    activeTab:
      this.accountTabMapping[this.props.match.params.activePage] ||
      this.accountTabMapping.next_delivery,
    subLabels: [],
    openPayBill: false,
  };

  static getDerivedStateFromProps(props, state) {
    const nextTab = ACCOUNT_TAB_MAPPING(props.isMobile, props.isSubscriber)[
      props.match.params.activePage
    ];

    if (nextTab !== state.activeTab) {
      return { activeTab: nextTab };
    }
    const { selectedServiceLocation } = props;

    if (selectedServiceLocation && selectedServiceLocation.nextDeliveryDate) {
      return {
        subLabels: map(
          ACCOUNT_ROUTES(props.isMobile, props.isSubscriber),
          (route) =>
            route.path === 'next-delivery'
              ? formatDate(selectedServiceLocation.nextDeliveryDate)
              : null,
        ),
      };
    }
    return null;
  }

  constructor(props) {
    super(props);

    this.state = {
      activeTab:
        ACCOUNT_TAB_MAPPING[this.props.match.params.activePage] ||
        ACCOUNT_TAB_MAPPING.next_delivery,
    };
  }

  setOpenBillDrawer = (open) => this.setState({ openPayBill: open });

  handleOpenPayBillDrawer = () => this.setOpenBillDrawer(true);

  handleClosePayBillDrawer = () => this.setOpenBillDrawer(false);

  handleTabChange = (value) => {
    if (!isEmpty(this.props.selectedServiceLocation)) {
      this.props.history.push(
        INVERTED_ACCOUNT_TABS(this.props.isMobile, this.props.isSubscriber)[
          value
        ],
      );
    }
  };

  handleOpenAutopay = () => this.props.showDrawer(AUTOPAY_SIGN_UP);

  handleCloseAutopay = () => this.props.hideDrawer(AUTOPAY_SIGN_UP);

  render() {
    const {
      props: { loading, siteName, classes, width, isSubscriber },
      state: { activeTab, subLabels, openPayBill },
    } = this;

    const isMobile = isWidthDown('sm', width);

    const TAB_TITLES = map(
      ACCOUNT_ROUTES(isMobile, isSubscriber),
      (route) => route.name,
    );

    const TAB_CONTENT = map(
      ACCOUNT_ROUTES(isMobile, isSubscriber),
      (route) => route.component,
    );

    const currentRoute = find(
      ACCOUNT_ROUTES(isMobile, isSubscriber),
      (value) =>
        value.path === INVERTED_ACCOUNT_TABS(isMobile, isSubscriber)[activeTab],
    );

    const title = currentRoute ? currentRoute.name : siteName;

    return (
      <>
        {isSubscriber && (
          <>
            <MarketingPromotion />
          </>
        )}
        <Loader loading={loading}>
          <AccountHeader
            openPayBillDrawer={this.handleOpenPayBillDrawer}
            openAutopayDrawer={this.handleOpenAutopay}
          />
          <Tabs
            value={activeTab}
            classes={classes}
            tabLabels={TAB_TITLES}
            tabSubLabels={subLabels}
            content={TAB_CONTENT}
            onChange={this.handleTabChange}
          />
          {openPayBill && (
            <PayBillDrawer
              open={openPayBill}
              onClose={this.handleClosePayBillDrawer}
            />
          )}
          <AutopaySignUpDrawer
            drawerProps={{ onClose: this.handleCloseAutopay }}
            formProps={{ onClose: this.handleCloseAutopay }}
          />
          <Helmet title={`${title} | ${siteName}`} />
        </Loader>
      </>
    );
  }
}

PrimoAccount.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  siteName: PropTypes.string.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  selectedServiceLocation: PropTypes.object,
  showDrawer: PropTypes.func,
  hideDrawer: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  siteName: selectSiteName(),
  loading: selectIsLoading(),
  selectedServiceLocation: selectSelectedServiceLocation(),
  isSubscriber: selectIsSubscriber(),
});

const mapDispatchToProps = {
  showDrawer,
  hideDrawer,
};

export default compose(
  withStyles(jsStyles),
  withWidth(),
  connect(mapStateToProps, mapDispatchToProps),
)(PrimoAccount);
